import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Fuel, Droplet, IndianRupee, TrendingUp, Camera, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CapturePhoto, MultiCapturePhoto } from '@/components/transport/CapturePhoto';
import { transportApi } from '@/services/transportApi';
import { KpiTile, DateRangeFilter, inDateRange, OwnershipFilter, ownershipParams, HiredBadge } from '@/components/transport/DashboardBits';
import { AddBillsDialog } from '@/components/transport/AddBillsDialog';

// Km/L for the fills in view: per vehicle (last good odometer − first good odometer) ÷ litres filled after the first.
const rangeKmpl = (rows) => {
  const byVeh = {};
  rows.forEach(r => { (byVeh[r.vehicle_id] = byVeh[r.vehicle_id] || []).push(r); });
  let km = 0, litres = 0;
  Object.values(byVeh).forEach(list => {
    const s = [...list].sort((a, b) => `${a.date}${a.created_at}`.localeCompare(`${b.date}${b.created_at}`));
    const good = s.map((r, i) => (r.odo_issue ? -1 : i)).filter(i => i >= 0);
    if (good.length < 2) return;
    const [a, b] = [good[0], good[good.length - 1]];
    km += Number(s[b].odometer) - Number(s[a].odometer);
    litres += s.slice(a + 1, b + 1).reduce((t, r) => t + (Number(r.litres) || 0), 0);
  });
  return litres ? km / litres : null;
};

const ODO_ISSUE = { missing: 'No odometer', not_higher: 'Odometer not above last fill' };
const KmRun = ({ r, p = 'fuel' }) => (r.odo_issue
  ? <span className="text-[11px] text-gray-400" data-testid={`${p}-odo-issue-${r.id}`}>{ODO_ISSUE[r.odo_issue]}</span>
  : r.km_since_last != null
    ? <span data-testid={`${p}-km-${r.id}`}>{r.km_since_last.toLocaleString('en-IN')}<span className="block text-[11px] text-gray-500">since {Number(r.prev_odometer).toLocaleString('en-IN')}</span></span>
    : <span className="text-[11px] text-gray-400" data-testid={`${p}-first-fill-${r.id}`}>First fill</span>);

const LowNote = ({ r, p = 'fuel' }) => (r.low_mileage_alert ? (
  <span className="block text-[11px] font-normal text-red-600" data-testid={`${p}-low-${r.id}`}
    title={r.low_mileage_alert.email_error ? `Email not sent: ${r.low_mileage_alert.email_error}` : 'Managers alerted'}>
    {r.low_mileage_alert.drop_pct}% below usual {r.low_mileage_alert.usual_kmpl}</span>) : null);

const Stat = ({ label, children }) => (
  <div className="bg-gray-50 rounded-lg px-2.5 py-1.5"><p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p><div className="text-sm font-medium text-[#1A1F2E]">{children}</div></div>
);

// Phone layout: one card per fill with a full-width bill capture button.
const FuelCard = ({ r, onBills }) => (
  <Card className="border-0 shadow-sm" data-testid={`fuel-card-${r.id}`}><CardContent className="p-4 space-y-3">
    <div className="flex justify-between items-start gap-2">
      <div className="min-w-0"><p className="font-semibold text-[#1A1F2E]">{r.vehicle_label}<HiredBadge ownership={r.vehicle_ownership} testId={`fuel-card-hired-${r.id}`} /></p>
        <p className="text-xs text-gray-500">{r.date} · {r.station || 'No station'}</p></div>
      <p className="font-bold text-[#1A1F2E] whitespace-nowrap">₹{r.amount}</p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <Stat label="Odometer">{r.odometer || '—'}</Stat><Stat label="Litres">{r.litres}</Stat>
      <Stat label="Km run"><KmRun r={r} p="fuel-card" /></Stat>
      <Stat label="Km/L"><span data-testid={`fuel-card-kmpl-${r.id}`}>{r.kmpl ?? '—'}</span><LowNote r={r} p="fuel-card" /></Stat>
    </div>
    <div className="flex flex-wrap gap-3 text-xs">
      {r.odo_photo_url && <a href={r.odo_photo_url} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`fuel-card-odo-photo-${r.id}`}>Odometer photo</a>}
      {(r.bill_photo_urls || []).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`fuel-card-bill-${r.id}-${i}`}>Bill {i + 1}</a>)}
    </div>
    <Button variant="outline" className="w-full h-11 border-amber-300" onClick={() => onBills(r)} data-testid={`fuel-card-add-bills-${r.id}`}>
      <Camera className="w-4 h-4 mr-2" />{(r.bill_photo_urls || []).length ? 'Add more bill photos' : 'Add bill photos'}
    </Button>
  </CardContent></Card>
);

const FuelLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [own, setOwn] = useState('all');
  const [form, setForm] = useState({ vehicle_id: '', odometer: '', litres: '', amount: '', station: '', date: '' });
  const [billPhotos, setBillPhotos] = useState([]);
  const [odoPhoto, setOdoPhoto] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filtered = useMemo(() => rows.filter(r => inDateRange(r.date, from, to)), [rows, from, to]);
  const kpis = useMemo(() => {
    const litres = filtered.reduce((s, r) => s + (Number(r.litres) || 0), 0);
    const amount = filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return { entries: filtered.length, litres, amount, avg: litres ? amount / litres : 0, kmpl: rangeKmpl(filtered) };
  }, [filtered]);

  const load = async () => { setRows(await transportApi.listFuel(ownershipParams(own))); setVehicles(await transportApi.listVehicles({ active: true })); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => toast.error('Failed to load fuel log')); }, [own]);
  const [saving, setSaving] = useState(false);
  const [billsFor, setBillsFor] = useState(null);

  const save = async () => {
    if (!form.vehicle_id || !form.litres || !form.amount) { toast.error('Vehicle, litres & amount required'); return; }
    setSaving(true);
    try {
      await transportApi.createFuel({ ...form, odometer: Number(form.odometer || 0), litres: Number(form.litres), amount: Number(form.amount), bill_photos: billPhotos, odo_photo: odoPhoto });
      toast.success('Fuel entry added'); setOpen(false); setForm({ vehicle_id: '', odometer: '', litres: '', amount: '', station: '', date: '' }); setBillPhotos([]); setOdoPhoto(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed — please check your connection and try again'); load(); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-fuel-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-12 pb-3 md:py-0 md:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10 px-2 md:px-4" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold font-display">Fuel Log</h1>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="fuel-add-btn"><Plus className="w-4 h-4 mr-1" />Add Fuel</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="fuel-kpis">
          <KpiTile label="Entries" value={kpis.entries} icon={Fuel} accent="slate" testId="fuel-kpi-entries" />
          <KpiTile label="Total Litres" value={kpis.litres.toLocaleString('en-IN', { maximumFractionDigits: 1 })} icon={Droplet} accent="blue" testId="fuel-kpi-litres" />
          <KpiTile label="Total Amount" value={`₹${kpis.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} icon={IndianRupee} accent="green" testId="fuel-kpi-amount" />
          <KpiTile label="Avg ₹/Litre" value={`₹${kpis.avg.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} icon={TrendingUp} accent="amber" testId="fuel-kpi-avg" />
          <KpiTile label="Km/L (fuel odometer)" value={kpis.kmpl != null ? kpis.kmpl.toFixed(2) : '—'} icon={Gauge} accent="teal" testId="fuel-kpi-kmpl" />
        </div>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(''); setTo(''); }} testPrefix="fuel">
          <OwnershipFilter value={own} onChange={setOwn} testPrefix="fuel" />
        </DateRangeFilter>
        <div className="md:hidden space-y-3" data-testid="fuel-cards">
          {filtered.map(r => <FuelCard key={r.id} r={r} onBills={setBillsFor} />)}
          {filtered.length === 0 && <p className="text-center text-gray-400 py-8" data-testid="fuel-cards-empty">No fuel entries in this range.</p>}
        </div>
        <Card className="border-0 shadow-md hidden md:block"><CardContent className="p-0">
          <Table data-testid="fuel-table">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Vehicle</TableHead><TableHead>Odometer</TableHead>
              <TableHead>Litres</TableHead><TableHead>Km run</TableHead><TableHead>Km/L</TableHead><TableHead>Amount (₹)</TableHead><TableHead>Station</TableHead><TableHead>Odo Photo</TableHead><TableHead>Bill(s)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} data-testid={`fuel-row-${r.id}`}>
                  <TableCell>{r.date}</TableCell><TableCell className="whitespace-nowrap">{r.vehicle_label}<HiredBadge ownership={r.vehicle_ownership} testId={`fuel-hired-${r.id}`} /></TableCell><TableCell>{r.odometer}</TableCell>
                  <TableCell>{r.litres}</TableCell><TableCell><KmRun r={r} /></TableCell>
                  <TableCell className="font-semibold" data-testid={`fuel-kmpl-${r.id}`}>{r.kmpl != null ? r.kmpl : '—'}<LowNote r={r} /></TableCell>
                  <TableCell>₹{r.amount}</TableCell><TableCell>{r.station || '—'}</TableCell>
                  <TableCell>{r.odo_photo_url ? <a href={r.odo_photo_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs" data-testid={`fuel-odo-photo-${r.id}`}>View</a> : '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">{(r.bill_photo_urls && r.bill_photo_urls.length) ? r.bill_photo_urls.map((u, i) => (<a key={i} href={u} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs mr-2" data-testid={`fuel-bill-${r.id}-${i}`}>Bill {i + 1}</a>)) : <span className="text-gray-400 mr-2">—</span>}
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-amber-300" onClick={() => setBillsFor(r)} data-testid={`fuel-add-bills-${r.id}`}><Camera className="w-3 h-3 mr-1" />Add</Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-gray-400 py-8" data-testid="fuel-empty">No fuel entries in this range.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="fuel-dialog" aria-describedby={undefined} className="max-h-[calc(100dvh-6rem)] sm:max-h-[92dvh] overflow-y-auto overflow-x-hidden overscroll-contain w-[calc(100vw-1rem)] sm:w-full rounded-lg p-4 sm:p-6">
          <DialogHeader><DialogTitle>Add Fuel Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 min-w-0">
            <div><Label>Vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}><SelectTrigger data-testid="fuel-vehicle-select"><SelectValue placeholder="Vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
              <div className="min-w-0"><Label>Date</Label><Input type="date" className="min-w-0 w-full" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="fuel-date-input" /></div>
              <div className="min-w-0"><Label>Odometer (km)</Label><Input type="number" inputMode="numeric" value={form.odometer} onChange={e => setForm(f => ({ ...f, odometer: e.target.value }))} data-testid="fuel-odo-input" /></div>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
              <div><Label>Litres</Label><Input type="number" inputMode="decimal" value={form.litres} onChange={e => setForm(f => ({ ...f, litres: e.target.value }))} data-testid="fuel-litres-input" /></div>
              <div><Label>Amount (₹)</Label><Input type="number" inputMode="decimal" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="fuel-amount-input" /></div>
            </div>
            <div><Label>Fuel Station</Label><Input value={form.station} onChange={e => setForm(f => ({ ...f, station: e.target.value }))} data-testid="fuel-station-input" /></div>
            <div><Label>Odometer Photo</Label><CapturePhoto label="Photograph odometer" onCapture={setOdoPhoto} captured={!!odoPhoto} testId="fuel-odo-photo" /></div>
            <div><Label>Bill &amp; Slip Photos</Label><MultiCapturePhoto label="Photograph fuel bill / transaction slip" photos={billPhotos} onChange={setBillPhotos} testId="fuel-bill-photos" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="fuel-save-btn">{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AddBillsDialog row={billsFor} kind="fuel" onClose={() => setBillsFor(null)} onDone={load} />
    </div>
  );
};

export default FuelLog;
